import { useState, useEffect } from 'react';
import api from '@/lib/api';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import {
  Building2, CheckCircle2, XCircle, Loader2, Save, PlugZap, Eye, EyeOff,
  HelpCircle, ChevronDown, ChevronUp, ExternalLink,
} from 'lucide-react';

/**
 * Admin-only settings card to wire Reno Resolve to Chorus Pro (via PISTE).
 * Secrets are stored encrypted backend-side; the GET response never echoes them
 * back, only a "set / not set" flag. Leave a secret field empty on save to keep
 * the existing value.
 */
export default function ChorusSettingsCard() {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [data, setData] = useState(null);
  const [form, setForm] = useState({
    environment: 'sandbox',
    client_id: '',
    client_secret: '',
    technical_login: '',
    technical_password: '',
    structure_id: '',
    bank_code: '',
    user_id: '',
  });
  const [showSecret, setShowSecret] = useState(false);
  const [showPwd, setShowPwd] = useState(false);
  const [showHelp, setShowHelp] = useState(false);
  const [lastTestResult, setLastTestResult] = useState(null); // 'ok' | 'ko' | null
  const [lastTestMessage, setLastTestMessage] = useState('');

  const load = async () => {
    try {
      const r = await api.get('/chorus/settings');
      setData(r.data);
      setForm((p) => ({
        ...p,
        environment: r.data.environment || 'sandbox',
        client_id: r.data.client_id || '',
        technical_login: r.data.technical_login || '',
        structure_id: r.data.structure_id ?? '',
        bank_code: r.data.bank_code ?? '',
        user_id: r.data.user_id ?? '',
      }));
    } catch (e) {
      // Non-admin or first time – stay silent
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const handleChange = (k, v) => setForm((p) => ({ ...p, [k]: v }));

  const handleSave = async () => {
    setSaving(true);
    try {
      const body = {
        environment: form.environment,
        client_id: form.client_id.trim(),
        technical_login: form.technical_login.trim(),
        structure_id: form.structure_id === '' ? null : Number(form.structure_id),
        bank_code: form.bank_code === '' ? null : Number(form.bank_code),
        user_id: form.user_id === '' ? null : Number(form.user_id),
      };
      // Only send secrets if the user actually typed something
      if (form.client_secret) body.client_secret = form.client_secret;
      if (form.technical_password) body.technical_password = form.technical_password;
      await api.put('/chorus/settings', body);
      toast.success('Paramètres Chorus enregistrés');
      setForm((p) => ({ ...p, client_secret: '', technical_password: '' }));
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erreur lors de l'enregistrement");
    }
    setSaving(false);
  };

  const handleTest = async () => {
    setTesting(true);
    setLastTestResult(null);
    try {
      const r = await api.post('/chorus/test-connection');
      const env = r?.data?.environment || form.environment;
      const tp = r?.data?.token_preview || '';
      const msg = `Connexion Chorus OK (${env})${tp ? ` — token ${tp}` : ''}`;
      setLastTestResult('ok');
      setLastTestMessage(msg);
      toast.success(msg);
    } catch (e) {
      // detail peut être string, objet ou tableau (validation FastAPI) — toujours stringifier
      let detail = e?.response?.data?.detail;
      if (detail && typeof detail !== 'string') {
        try { detail = JSON.stringify(detail); } catch (_) { detail = String(detail); }
      }
      const msg = detail || e?.message || 'Échec de la connexion PISTE';
      setLastTestResult('ko');
      setLastTestMessage(msg);
      try { toast.error(msg, { duration: 8000 }); } catch (_) { /* ignore toast crash */ }
    } finally {
      setTesting(false);
    }
  };

  if (loading) return null;
  if (!data) return null; // not admin or fetch failed

  const configured = data.configured;

  return (
    <div className="bg-white rounded-lg p-6 shadow-sm border border-slate-200" data-testid="chorus-settings-card">
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <div className="w-12 h-12 bg-purple-100 rounded-lg flex items-center justify-center">
            <Building2 className="w-6 h-6 text-purple-600" />
          </div>
          <div>
            <h2 className="text-lg font-semibold text-slate-900">Chorus Pro (PISTE)</h2>
            <div className="flex items-center gap-2 mt-1">
              {configured ? (
                <>
                  <CheckCircle2 className="w-4 h-4 text-green-600" />
                  <span className="text-sm text-green-600 font-medium">Configuré</span>
                </>
              ) : (
                <>
                  <XCircle className="w-4 h-4 text-slate-400" />
                  <span className="text-sm text-slate-500">Non configuré</span>
                </>
              )}
              <Badge variant="outline" className="text-xs">
                {form.environment === 'production' ? 'PRODUCTION' : 'Sandbox'}
              </Badge>
            </div>
          </div>
        </div>
        <Button
          variant="outline"
          onClick={handleTest}
          disabled={testing || !configured}
          data-testid="chorus-test-btn"
        >
          {testing
            ? <Loader2 key="chorus-test-loader" className="w-4 h-4 mr-2 animate-spin" />
            : <PlugZap key="chorus-test-icon" className="w-4 h-4 mr-2" />}
          Tester la connexion
        </Button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <Field label="Environnement">
          <select
            value={form.environment}
            onChange={(e) => handleChange('environment', e.target.value)}
            className="w-full h-9 rounded-md border border-slate-200 px-3 text-sm bg-white"
            data-testid="chorus-env-select"
          >
            <option value="sandbox">Sandbox (tests)</option>
            <option value="production">Production</option>
          </select>
        </Field>

        <Field label="Client ID PISTE">
          <Input
            value={form.client_id}
            onChange={(e) => handleChange('client_id', e.target.value)}
            placeholder="ex: 5f3a..."
            data-testid="chorus-client-id"
          />
        </Field>

        <Field
          label="Client Secret PISTE"
          hint={data.client_secret_set ? '(défini — laissez vide pour conserver)' : ''}
        >
          <div className="relative">
            <Input
              type={showSecret ? 'text' : 'password'}
              value={form.client_secret}
              onChange={(e) => handleChange('client_secret', e.target.value)}
              placeholder={data.client_secret_set ? '••••••••' : 'Saisir le secret'}
              data-testid="chorus-client-secret"
            />
            <button type="button" onClick={() => setShowSecret((v) => !v)}
              className="absolute right-2 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600">
              {showSecret ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
            </button>
          </div>
        </Field>

        <Field label="Login technique Chorus">
          <Input
            value={form.technical_login}
            onChange={(e) => handleChange('technical_login', e.target.value)}
            placeholder="email@chorus..."
            data-testid="chorus-tech-login"
          />
        </Field>

        <Field
          label="Mot de passe technique Chorus"
          hint={data.technical_password_set ? '(défini — laissez vide pour conserver)' : ''}
        >
          <div className="relative">
            <Input
              type={showPwd ? 'text' : 'password'}
              value={form.technical_password}
              onChange={(e) => handleChange('technical_password', e.target.value)}
              placeholder={data.technical_password_set ? '••••••••' : 'Saisir le mot de passe'}
              data-testid="chorus-tech-password"
            />
            <button type="button" onClick={() => setShowPwd((v) => !v)}
              className="absolute right-2 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600">
              {showPwd ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
            </button>
          </div>
        </Field>

        <Field label="ID structure fournisseur" hint="(idFournisseur dans Chorus)">
          <Input
            type="number"
            value={form.structure_id}
            onChange={(e) => handleChange('structure_id', e.target.value)}
            placeholder="ex: 12345"
            data-testid="chorus-structure-id"
          />
        </Field>

        <Field label="Code coordonnées bancaires" hint="(facultatif)">
          <Input
            type="number"
            value={form.bank_code}
            onChange={(e) => handleChange('bank_code', e.target.value)}
            placeholder="ex: 6789"
          />
        </Field>

        <Field label="ID utilisateur Chorus" hint="(idUtilisateurCourant)">
          <Input
            type="number"
            value={form.user_id}
            onChange={(e) => handleChange('user_id', e.target.value)}
            placeholder="ex: 4242"
          />
        </Field>
      </div>

      <div className="mt-5 flex items-center justify-between gap-3">
        <p className="text-xs text-slate-500">
          Les secrets sont chiffrés en base. Récupérez vos identifiants sur{' '}
          <a href="https://piste.gouv.fr" target="_blank" rel="noreferrer" className="text-blue-600 hover:underline">piste.gouv.fr</a>
          {' '}(onglet Authentification de votre application).
        </p>
        <Button onClick={handleSave} disabled={saving} data-testid="chorus-save-btn">
          {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
          Enregistrer
        </Button>
      </div>

      {/* Last connection test feedback */}
      {lastTestResult && (
        <div
          className={`mt-4 rounded-lg border p-3 text-sm ${
            lastTestResult === 'ok'
              ? 'bg-emerald-50 border-emerald-200 text-emerald-900'
              : 'bg-red-50 border-red-200 text-red-900'
          }`}
          data-testid="chorus-test-result"
        >
          <div className="flex items-start gap-2">
            {lastTestResult === 'ok'
              ? <CheckCircle2 key="chorus-result-ok" className="w-4 h-4 mt-0.5 shrink-0" />
              : <XCircle key="chorus-result-ko" className="w-4 h-4 mt-0.5 shrink-0" />}
            <span className="flex-1 whitespace-pre-wrap break-words">{lastTestMessage}</span>
          </div>
        </div>
      )}

      {/* Troubleshooting / setup guide */}
      <div className="mt-4 border-t border-slate-200 pt-3">
        <button
          type="button"
          onClick={() => setShowHelp((v) => !v)}
          className="flex items-center gap-2 text-sm font-semibold text-slate-700 hover:text-blue-700"
          data-testid="chorus-help-toggle"
        >
          <HelpCircle className="w-4 h-4" />
          Comment obtenir mes identifiants PISTE & Chorus ?
          {showHelp ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
        </button>
        {showHelp && (
          <div className="mt-3 bg-blue-50/60 border border-blue-200 rounded-lg p-4 text-sm text-slate-700 space-y-3" data-testid="chorus-help-content">
            <div>
              <p className="font-semibold text-slate-900 mb-1">1. Créer une application sur PISTE</p>
              <ol className="list-decimal list-inside space-y-0.5 ml-1 text-slate-600">
                <li>Allez sur <a href="https://piste.gouv.fr" target="_blank" rel="noreferrer" className="text-blue-600 hover:underline inline-flex items-center gap-0.5">piste.gouv.fr <ExternalLink className="w-3 h-3" /></a> et connectez-vous</li>
                <li>Créez une « Application » (menu Mes applications)</li>
                <li>Notez bien si elle est créée en environnement <b>Sandbox</b> ou <b>Production</b></li>
              </ol>
            </div>
            <div>
              <p className="font-semibold text-slate-900 mb-1">2. Abonner l'application à l'API Chorus Pro</p>
              <ol className="list-decimal list-inside space-y-0.5 ml-1 text-slate-600">
                <li>Dans le catalogue d'API, recherchez <b>« Chorus Pro »</b></li>
                <li>Cliquez sur « S'abonner » et sélectionnez votre application</li>
                <li>Sans cet abonnement, PISTE renverra <code className="bg-red-100 px-1 rounded">invalid_client</code> 👉 erreur que vous voyez actuellement</li>
              </ol>
            </div>
            <div>
              <p className="font-semibold text-slate-900 mb-1">3. Récupérer client_id & client_secret</p>
              <ol className="list-decimal list-inside space-y-0.5 ml-1 text-slate-600">
                <li>Onglet <b>« Authentification »</b> de votre application sur PISTE</li>
                <li>Copiez <b>Client ID</b> (UUID) et <b>Client Secret</b> (cliquez sur l'œil pour le révéler)</li>
                <li>Collez-les ci-dessus puis <b>Enregistrer</b></li>
                <li>Vérifiez que l'environnement sélectionné (Sandbox / Production) correspond à votre app PISTE</li>
              </ol>
            </div>
            <div>
              <p className="font-semibold text-slate-900 mb-1">4. Compte technique Chorus Pro</p>
              <ol className="list-decimal list-inside space-y-0.5 ml-1 text-slate-600">
                <li>Sur <a href={form.environment === 'production' ? 'https://chorus-pro.gouv.fr' : 'https://qualif.chorus-pro.gouv.fr'} target="_blank" rel="noreferrer" className="text-blue-600 hover:underline inline-flex items-center gap-0.5">chorus-pro.gouv.fr <ExternalLink className="w-3 h-3" /></a>, créez un utilisateur dédié à l'intégration (Gestion des utilisateurs &gt; Nouvel utilisateur)</li>
                <li>Donnez-lui les habilitations <b>« Factures émises »</b> (dépôt + consultation)</li>
                <li>Renseignez son email/login et son mot de passe ci-dessus</li>
              </ol>
            </div>
            <div>
              <p className="font-semibold text-slate-900 mb-1">5. ID de structure fournisseur</p>
              <ol className="list-decimal list-inside space-y-0.5 ml-1 text-slate-600">
                <li>Sur Chorus Pro, ouvrez <b>Mes structures</b> &gt; votre structure</li>
                <li>L'<b>identifiant numérique</b> (visible en haut, ex : 12345) est à reporter dans « ID structure fournisseur »</li>
                <li>L'<b>ID utilisateur</b> se trouve dans le profil de l'utilisateur technique</li>
              </ol>
            </div>
            <div className="bg-amber-50 border border-amber-200 rounded p-2 text-xs text-amber-900">
              💡 Une fois tout rempli, cliquez sur <b>Tester la connexion</b> — si vous voyez un statut vert, vous pourrez envoyer / lier des factures à Chorus.
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function Field({ label, hint, children }) {
  return (
    <div>
      <label className="block text-xs uppercase tracking-wider text-slate-500 font-semibold mb-1">
        {label} {hint && <span className="normal-case font-normal text-slate-400">{hint}</span>}
      </label>
      {children}
    </div>
  );
}
